import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { updateETaxAutoSendSettings } from "../../../services";
import { EtaxAutoSendSettings } from "../../../types/etax-auto-send.type";
import { formatThaiShortDateTimeBkk } from "../../../utils/date";
import { HOUR_OPTIONS, WEEKDAY_OPTIONS, toHourOption } from "./autoSendMeta";

interface SettingsDialogProps {
  open: boolean;
  /** The stored settings; undefined while they are still loading or failed to load. */
  settings?: EtaxAutoSendSettings;
  onClose: () => void;
  onSaved: (settings: EtaxAutoSendSettings) => void;
}

interface Draft {
  enabled: boolean;
  weekday: number;
  hour: string;
}

const toDraft = (settings?: EtaxAutoSendSettings): Draft => ({
  enabled: settings?.enabled ?? false,
  weekday: settings?.weekday ?? 2,
  hour: toHourOption(settings?.start_time),
});

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

/**
 * The one company-wide auto-send schedule: on/off, the weekday and the whole hour (Bangkok).
 *
 * The dialog stays open after saving so the operator sees the round the new schedule
 * produces — the API computes `next_round_at`, and a schedule is only understood once it is
 * read back as a date.
 */
export default function SettingsDialog({
  open,
  settings,
  onClose,
  onSaved,
}: SettingsDialogProps) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(settings));
  const [saving, setSaving] = useState<boolean>(false);

  // Reset the draft on every open; a half-edited schedule from last time is not a setting.
  // `settings` is deliberately not a dependency: the saved response flows back through it,
  // and re-seeding then would be a no-op at best.
  useEffect(() => {
    if (open) setDraft(toDraft(settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Settings that arrive after the dialog opened seed it once they exist.
  useEffect(() => {
    if (open && settings && !saving) setDraft(toDraft(settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const stored = toDraft(settings);
  const dirty =
    draft.enabled !== stored.enabled ||
    draft.weekday !== stored.weekday ||
    draft.hour !== stored.hour;

  const handleSave = async () => {
    if (!settings || saving) return;
    setSaving(true);
    try {
      const {
        data: { data },
      } = await updateETaxAutoSendSettings({
        enabled: draft.enabled,
        weekday: draft.weekday,
        start_time: draft.hour,
      });
      onSaved(data);
    } catch {
      // Surfaced by the axios interceptor; keep the draft so the operator can retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        ตั้งค่าส่ง E-Tax อัตโนมัติ
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={saving}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!settings ? (
          <Typography color="text.secondary">ไม่พบข้อมูลการตั้งค่า</Typography>
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.enabled}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, enabled: e.target.checked }))
                  }
                />
              }
              label="เปิดใช้งาน"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ระบบจะส่งบิลค้างส่งที่ส่งได้ทั้งหมดสัปดาห์ละครั้งตามวันและเวลาด้านล่าง (เวลาไทย)
              ยกเว้นบิลที่ตั้งไม่ส่งอัตโนมัติไว้
            </Typography>

            <Box display="flex" gap={2}>
              <TextField
                select
                size="small"
                label="วัน"
                fullWidth
                value={draft.weekday}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, weekday: Number(e.target.value) }))
                }
              >
                {WEEKDAY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="เวลา"
                fullWidth
                value={draft.hour}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, hour: e.target.value }))
                }
              >
                {HOUR_OPTIONS.map((hour) => (
                  <MenuItem key={hour} value={hour}>
                    {hour}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.background.default,
              }}
            >
              <InfoRow
                label="รอบถัดไป"
                value={
                  settings.enabled
                    ? formatThaiShortDateTimeBkk(settings.next_round_at) || "-"
                    : "ปิดการส่งอัตโนมัติ"
                }
              />
              <InfoRow label="แก้ไขล่าสุดโดย" value={settings.updated_by ?? "-"} />
              <InfoRow
                label="แก้ไขล่าสุดเมื่อ"
                value={formatThaiShortDateTimeBkk(settings.updated_at) || "-"}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          ปิด
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!settings || !dirty}
          loading={saving}
        >
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
